import useRightPanelIndividualCall from 'components/Dashboard/Analytics/useRightPanelIndividualCall';
import thousandCommas from 'components/Dashboard/helpers/thousandCommas';
import ParliamentaryItem from 'components/Dashboard/ParliamentaryRecord/ParlamentaryItem';
import getUrlParam, { decodeUrlQueryValue } from 'components/Dashboard/utilities/getUrlParam';
import useIsMobile from 'components/Dashboard/utilities/useIsMobile';
import useOutsideElement from 'components/Dashboard/utilities/useOutsideElement';
import { useRef, useState, useEffect, useLayoutEffect } from 'react';
import { createDate } from 'components/Dashboard/Analytics/ui/MentionsByTopic/ChartDataTable';
import { sentimentSnapshotSectionValues } from 'components/Dashboard/Analytics/OverallSentimentRightPanel';
import { ContentItemsSectionsValues } from 'components/Dashboard/Analytics/ui/ContentItemsPanel';
import TweetItem from 'components/Dashboard/SocialMedia/TweetItem';

const StakeholderSentimentRightPanel = (props) => {
  const {
    contentState,
    analyticsState,
    stakeholderId,
    refs,
    setRightPanelState,
    StakeholderInformation,
    createSnapshotTextValue,
  } = props;

  const { mentionsDataSection, scrollBarRef } = refs ?? {};
  const { text, noResults, sentiment, date, dateFilters } = contentState;
  const { name } = StakeholderInformation ?? {};
  const { currentView } = analyticsState ?? {};

  const [isLoading, setIsLoading] = useState(!noResults);
  const [results, setResults] = useState([]);
  const [finalResults, setFinalResults] = useState([]);
  const [activeSentiment, setActiveSentiment] = useState(sentiment);
  const [sentimentText, setSentimentText] = useState(null);
  const [activeSection, setActiveSection] = useState();

  const callContentItems = useRef(null);
  const isLoadedComponent = useRef(false);
  const pageSize = 1000;
  const searchParameter = getUrlParam('search');
  const sectionsValues = useRef([]);
  const isMobile = useIsMobile();
  const sections = sentimentSnapshotSectionValues();

  const { individualApiCall } = useRightPanelIndividualCall({ analyticsState, contentState });

  callContentItems.current = async ({ source }) => {
    try {
      const afterReadyFunction = () => {
        setTimeout(() => {
          const offsetValue = isMobile ? 0 : 160;
          scrollBarRef?.current?.view?.scrollTo({
            top: mentionsDataSection.current?.offsetTop - offsetValue,
            behavior: 'smooth',
          });
        }, 300);
      };
      if (noResults) {
        setResults({ hits: [] });
        setFinalResults({ hits: [] });
        setIsLoading(false);
        sectionsValues.current = [];
        afterReadyFunction();
      } else {
        setSentimentText(null);
        setIsLoading(true);
        sectionsValues.current = [];

        // AE: looping per section (instead of one bare call) is what gives each type its correct
        // contentType/country filter - a plain type-less call resolves to 'ParliamentaryRecord' with
        // no contentType filter, which also pulls in Written questions via contactIds (the wrong field).
        let values = [];
        for (let i = 0; i < sections.length; i++) {
          let type = sections[i];
          const freeSearch = !!searchParameter ? decodeUrlQueryValue(searchParameter) : null;
          let results;
          try {
            results = await individualApiCall({
              type,
              ...(sentiment ? { sentiment, snapshot: true } : {}),
              freeSearch,
              source,
              stakeholderId,
              pageSize,
              dateFilters,
            });
          } catch (error) {}
          if (results) {
            values.push({
              name: type,
              value: results?.totalHits,
              results: { ...results, thereIsMoreContent: results.hits?.length === pageSize },
            });
          }
        }

        if (sentiment) {
          if (values.length > 0) {
            let finalValues = values.filter((item) => item.value > 0);
            if (finalValues.length > 0) {
              sectionsValues.current = finalValues;
              setActiveSection(finalValues[0]?.name);
              setResults(finalValues[0]?.results);
              setFinalResults(finalValues[0]?.results);
            } else {
              setResults({ hits: [] });
              setFinalResults({ hits: [] });
            }
            setIsLoading(false);
            afterReadyFunction();
            isLoadedComponent.current = true;
          }
        } else {
          const callresults = values.reduce(
            (acc, { results }) => ({
              hits: acc.hits.concat(results.hits ?? []),
              highlights: acc.highlights.concat(results.highlights ?? []),
              totalHits: acc.totalHits + (results.totalHits ?? 0),
            }),
            { hits: [], highlights: [], totalHits: 0 }
          );
          setResults(callresults);
          setFinalResults(callresults);
          setIsLoading(false);
          afterReadyFunction();
          isLoadedComponent.current = true;
        }
      }
    } catch (error) {}
  };

  useEffect(() => {
    const source = new AbortController();
    callContentItems.current({ source });
    return () => {
      source.abort('Content items panel cancelled by the user');
    };
  }, [contentState]);

  useEffect(() => {
    if (activeSentiment !== sentiment) {
      setActiveSentiment(sentiment);
    }
  }, [sentiment]);

  useEffect(() => {
    if (isLoadedComponent.current && sectionsValues?.current?.length > 0) {
      let section = sectionsValues.current.find((item) => item.name === activeSection);
      setIsLoading(true);
      setResults(noResults ? { hits: [] } : section?.results);
      setFinalResults(noResults ? { hits: [] } : section?.results);
      setTimeout(() => setIsLoading(false), 300);
    }
  }, [activeSection]);

  const filterFunction = ({ option }) => {
    if (!!option) {
      if (!sentiment) {
        const filteredResults = results?.hits?.filter(
          (item) => item?.sentiment?.toLowerCase() === option?.toLowerCase()
        );
        setFinalResults({
          ...results,
          hits: filteredResults,
        });
        const totalValue = results?.hits?.length;
        const value = filteredResults?.length;
        const percentage = (value * 100) / totalValue;
        let text = `${value ? thousandCommas(value) : 0} of ${thousandCommas(totalValue)} ${`contribution${
          totalValue === 1 ? '' : 's'
        } (${Math.round(percentage ?? 0)}%) by ${name}`} ${createDate(currentView, date)} were ${option.toLowerCase()}`;
        if (value === 0) {
          text = `No contributions by ${name} ${createDate(currentView, date)} were ${option.toLowerCase()}`;
        }
        setSentimentText(text);
      } else {
        setSentimentText(null);
        setRightPanelState({ ...contentState, sentiment: option, text: createSnapshotTextValue(option) });
      }
    }
  };

  return (
    <>
      <div className='flex-centered justify-content-between py-3 border-bottom'>
        <div>
          <h3 className={`analytics-panel-title title-h5-m-bold bg-white mb-1`}>
            {sentiment ?? 'Relevant'} contributions
          </h3>
          <p className='mb-0'>{sentimentText ?? text}</p>
        </div>

        <StakeholderSentimentFilter
          filterFunction={filterFunction}
          activeSentimentState={{ activeSentiment, setActiveSentiment }}
        />
      </div>
      <div className={`py-3`}>
        <div className='pb-3'>
          <ContentItemsSectionsValues
            sectionsValues={sectionsValues.current}
            setActiveSection={setActiveSection}
            activeSection={activeSection}
            sentimentPage={true}
          />
        </div>
        {isLoading ? (
          <p>Loading...</p>
        ) : (
          <>
            <div className='latest-tweets-list policy-list px-0 items-list legislation-content pb-2'>
              {finalResults?.hits?.map((item, index, array) => {
                const { type, contentUrl, contributionId } = item;
                const baseRow = { listContext: { index, item, array } };
                switch (type) {
                  case 'ParliamentaryRecord':
                  case 'WrittenQuestion':
                    return (
                      <ParliamentaryItem
                        key={`${type}-item-${index}`}
                        listRow={{
                          ...baseRow,
                          interactions: {
                            ...(baseRow.interactions ?? {}),
                            itemOnclick: (e) => {
                              e.preventDefault();
                              window.open(
                                `${contentUrl}${getUrlParam('topic-id') ? `?topic-id=${getUrlParam('topic-id')}` : ''}${
                                  !!contributionId ? `#contribution-${contributionId}` : ''
                                }`
                              );
                            },
                          },
                        }}
                      />
                    );

                  case 'Tweet':
                    return (
                      <TweetItem
                        key={`${type}-item-${index}`}
                        listRow={{
                          ...baseRow,
                          interactions: {
                            ...(baseRow.interactions ?? {}),
                            itemActionsProps: { onlyDots: true },
                          },
                        }}
                      />
                    );
                  default:
                    return '';
                }
              })}
              {finalResults?.hits?.length === 0 && (
                <p className='text-center main-light-text specific-top-padding-30 specific-y-margin-50'>
                  No contributions
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
};

const StakeholderSentimentFilter = ({ activeSentimentState, filterFunction }) => {
  const { activeSentiment, setActiveSentiment } = activeSentimentState ?? {};
  const [showOptions, setShowOptions] = useState(false);
  const [topPosition, setTopPosition] = useState(0);
  const [dropdownReady, setDropdownReady] = useState(false);
  const options = ['Positive', 'Negative', 'Neutral', 'Mixed'];
  const filtersElement = useRef(null);
  const dropdownRef = useRef(null);
  const hideFilters = () => {
    setShowOptions(false);
  };
  useOutsideElement(filtersElement, hideFilters);

  useLayoutEffect(() => {
    if (showOptions) {
      const buttonRect = filtersElement.current.getBoundingClientRect();
      const dropdownHeight = dropdownRef.current ? dropdownRef.current.offsetHeight : 200;
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - buttonRect.bottom;
      if (spaceBelow < dropdownHeight) {
        setTopPosition(-(dropdownHeight + 10));
      } else {
        setTopPosition(46);
      }
      setDropdownReady(true);
    } else {
      setDropdownReady(false);
    }
  }, [showOptions]);

  return (
    <div className='position-relative' ref={filtersElement}>
      <button
        className={`sentiment-bar-filter-button bg-main-white circle-button-35 border general-button rounded-button py-1 paragraph-p3 hovered-button ${
          showOptions ? 'bg-blue-hovered' : ''
        } flex-centered justify-content-center nowrap-item`}
        onClick={() => setShowOptions(!showOptions)}
      >
        <span className='sentiment-bar-text'>
          {activeSentiment ? (
            <span className={`d-inline-block mt-1 sentiment-dot sentiment-dot-${activeSentiment.toLowerCase()}`} />
          ) : (
            <span className={`icon-filters`} />
          )}
        </span>
      </button>
      {showOptions && (
        <div
          className={`box filters-container px-0 pt-0`}
          ref={dropdownRef}
          style={{
            width: '250px',
            top: dropdownReady ? `${topPosition}px` : '-9999px',
            visibility: dropdownReady ? 'visible' : 'hidden',
          }}
        >
          <p className='border-bottom py-3 px-4 mb-0 font-weight-bold'>Filter contributions by stakeholder sentiment</p>
          <div className='py-1'>
            {options.map((option) => {
              return (
                <button
                  key={`filter-${option}`}
                  className='general-button text-left px-4 py-2 list-action-button d-block w-100'
                  onClick={() => {
                    setShowOptions(false);
                    setActiveSentiment(option);
                    filterFunction({ option });
                  }}
                >
                  <span className={`sentiment-dot sentiment-dot-${option.toLowerCase()} d-inline-block mr-2`} />{' '}
                  {option} contributions
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default StakeholderSentimentRightPanel;
